import { useCallback, useEffect, useMemo, useState } from "react";
import { AUTH_STORAGE_KEYS } from "../api/authApi";
import {
  UnauthorizedError,
  fetchExpiringKeys,
  fetchPartners,
} from "../api/licencesApi";
import { getPartnerDiscounts } from "../utils/partnerPricing";

const PAGE_SIZE = 10;

export function useLicences({ onUnauthorized }) {
  const [licences, setLicences] = useState([]);
  const [partners, setPartners] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const partnerId = localStorage.getItem(AUTH_STORAGE_KEYS.partnerId);

  const refresh = useCallback(async () => {
    if (!partnerId) {
      onUnauthorized();
      return;
    }

    setLoading(true);
    setError("");
    try {
      const [items, partnersResult] = await Promise.all([
        fetchExpiringKeys(partnerId),
        fetchPartners(partnerId),
      ]);

      setLicences(Array.isArray(items) ? items : []);

      setPartners(Array.isArray(partnersResult) ? partnersResult : []);
      setPage(1);
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }

      console.error("Failed to load licences:", err);

      setError(err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [partnerId, onUnauthorized]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const totalPages = Math.max(1, Math.ceil(licences.length / PAGE_SIZE));
  const pageItems = licences.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const partnerDiscounts = useMemo(() => {
    return getPartnerDiscounts(partners, partnerId);
  }, [partners, partnerId]);

  const goToPage = useCallback(
    (nextPage) => {
      setPage(Math.min(Math.max(1, nextPage), totalPages));
    },
    [totalPages],
  );

  return {
    pageItems,
    page,
    totalPages,
    partners,
    licenseDiscountPercent: partnerDiscounts.licenseDiscountPercent,
    hostingDiscountPercent: partnerDiscounts.hostingDiscountPercent,
    loading,
    error,
    setError,
    goToPage,
    refresh,
  };
}
